/** venture/Step15Fee.tsx — 15단계 「수수료 청구」: 회사별 청구 예정액 · 청구처(업체/FP 대납)별 묶음 · 항목별 청구/입금 체크 (2026-09-24)
 *  금액은 등록정보의 회사별 수수료 조건에서 계산한다. 체크 저장소는 구 s11(billed/paid)을 그대로 써서 기존 진행 데이터가 살아 있다. */
import React from 'react';
import { getChk, mutStore } from './stepcommon';
import CoStatBar from './CoStatBar';
import { regCoOf, feeOf, feeTotals, fmtWon, fpLabel, TIMING_KO, BILLTO_KO } from './coterms';
import { feeExtrasFor } from './types';
import type { Project, CoCard, FeeBillTo } from './types';

interface Props { p: Project; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }
function colCls(cnt: number): string { return cnt >= 3 ? 'cols3' : (cnt === 2 ? 'cols2' : 'cols1'); }

export default function Step15Fee({ p, onPatch, toast }: Props) {
  const cos: CoCard[] = (p.collect && p.collect.cos) || [];
  const toggle = (key: string) => onPatch(mutStore(p, 's11', (s) => { s.chk = s.chk || {}; s.chk[key] = !s.chk[key]; }));
  if (!cos.length) return <div className="sact"><div className="sact-h">💰 수수료 청구</div><div className="lbl" style={{ padding: '14px 0', textAlign: 'center' }}>등록된 투자법인이 없습니다</div></div>;

  // 프로젝트 합계 — 청구처별
  const sum: Record<FeeBillTo, number> = { company: 0, fp: 0 };
  cos.forEach((co) => { const rc0 = regCoOf(p, co); const t = feeTotals(feeOf(rc0), feeExtrasFor(rc0)); sum.company += t.byBillTo.company; sum.fp += t.byBillTo.fp; });

  return (
    <div className="sact">
      <div className="sact-h">💰 수수료 청구</div>
      <div className="lbl">회사별 수수료 조건(등록정보)으로 청구 예정액을 계산합니다 · 업체 청구 {fmtWon(sum.company)} · FP 대납 {fmtWon(sum.fp)}</div>
      <div className={'cogrid ' + colCls(cos.length)}>
        {cos.map((co) => {
          const rc = regCoOf(p, co);
          const fee = feeOf(rc);
          const t = feeTotals(fee, feeExtrasFor(rc));
          const fp = fpLabel(rc);
          const rowsCo = t.lines.filter((l) => !l.included && l.billTo === 'company');
          const rowsFp = t.lines.filter((l) => !l.included && l.billTo === 'fp');
          const included = t.lines.filter((l) => l.included);
          const chk = (slot: string) => getChk(p, 's11', co.id + '_' + slot);
          // 벤처 수수료 본건은 구 키(billed/paid) 그대로, 부대비용은 billed_<항목>/paid_<항목>
          const Line = ({ slot, label, amount, sub }: { slot: string; label: string; amount: number; sub?: string }) => {
            const bill = slot ? 'billed_' + slot : 'billed';
            const pay = slot ? 'paid_' + slot : 'paid';
            return (
              <div className="feeline">
                <span className="fl">{label}{sub ? <span className="s3muted"> · {sub}</span> : null}</span>
                <span className="fa mono">{amount ? fmtWon(amount) : '금액 미정'}</span>
                <button className={'chkpill sm' + (chk(bill) ? ' on' : '')} onClick={() => toggle(co.id + '_' + bill)}>{chk(bill) ? '✓ 청구' : '청구'}</button>
                <button className={'chkpill sm' + (chk(pay) ? ' on' : '')} onClick={() => toggle(co.id + '_' + pay)}>{chk(pay) ? '✓ 입금' : '입금'}</button>
              </div>
            );
          };
          const group = (title: string, who: string, lines: React.ReactNode, total: number) => (
            <div className="feegrp">
              <div className="feegrp-h"><b>{title}</b><span className="s3muted">{who}</span><span className="grow" /><span className="mono">{fmtWon(total)}</span></div>
              {lines}
            </div>
          );
          return (
            <div className="cocol" key={co.id} id={'co-' + co.id + '-15'}>
              <div className="cocol-h">🏢 {co.name || '(법인)'}</div>
              <CoStatBar p={p} co={co} n={15} onPatch={onPatch} toast={toast} />
              {!rc ? <div className="s3file"><span className="s3muted">등록정보에서 이 법인을 찾을 수 없습니다 — 등록정보 탭에서 확인해 주세요</span></div> : null}
              {fee.billTo === 'company' || rowsCo.length ? group('업체 청구', co.name || '', (<>
                {fee.billTo === 'company' ? <Line slot="" label="벤처 수수료" amount={t.ventureTotal} sub={TIMING_KO[fee.timing] + (t.vat ? ' · 부가세 ' + fmtWon(t.vat) + ' 포함' : ' · 부가세 포함가')} /> : null}
                {rowsCo.map((l) => <Line key={l.key} slot={l.key} label={l.label} amount={l.amount} />)}
              </>), t.byBillTo.company) : null}
              {fee.billTo === 'fp' || rowsFp.length ? group('FP 대납', fp || 'FP 미지정 — 등록정보에서 소개 FP를 입력해 주세요', (<>
                {fee.billTo === 'fp' ? <Line slot="" label="벤처 수수료" amount={t.ventureTotal} sub={TIMING_KO[fee.timing] + (t.vat ? ' · 부가세 ' + fmtWon(t.vat) + ' 포함' : '')} /> : null}
                {rowsFp.map((l) => <Line key={l.key} slot={l.key} label={l.label} amount={l.amount} />)}
              </>), t.byBillTo.fp) : null}
              {included.length ? <div className="snote" style={{ marginTop: 6 }}>벤처 수수료에 포함: {included.map((l) => l.label).join(' · ')}</div> : null}
              {fee.memo ? <div className="snote">비고: {fee.memo}</div> : null}
              <div className="snote" style={{ marginTop: 4 }}>
                {feeExtrasFor(rc).map(([k, label]) => fee[k].mode === 'separate' && !fee[k].amount ? label + ' 금액 미정 · ' : '').join('')}
                {BILLTO_KO[fee.billTo]} · 조건 변경은 등록정보 탭
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
